import {
  IInvoiceAdjustmentRequest,
  IInvoiceAdjustmentSummary,
  InvoiceAdjustmentRequestStatus,
  InvoiceAdjustmentRequestType,
} from "@/api_framework/api_modals/payment";
import { formatAmount } from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/utils/confirmationHelpers";
import StatusBadge from "@/app/components/ui/Badges/StatusBadge";
import SecondaryButton from "@/app/components/ui/Buttons/SecondaryButton";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import GenericBulkUploadModal from "@/app/components/ui/Modals/GenericBulkUploadModal";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import PaginationComponent from "@/app/components/ui/Pagination";
import TableComponent, { TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useInvoiceAdjustmentRequestDetailsAPI } from "@/app/hooks/api_hooks/payment/useInvoiceAdjustmentRequestDetailsAPI";
import { useInvoiceAdjustmentRequestsAPI } from "@/app/hooks/api_hooks/payment/useInvoiceAdjustmentRequestsAPI";
import { useInvoiceAdjustmentSummaryAPI } from "@/app/hooks/api_hooks/payment/useInvoiceAdjustmentSummaryAPI";
import { useInvoiceAdjustmentUploadAPI } from "@/app/hooks/api_hooks/payment/useInvoiceAdjustmentUploadAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useUtils } from "@/app/hooks/useUtils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FINISHED_STATUSES,
  INVOICE_ADJUSTMENT_MAX_ROWS,
  IN_FLIGHT_STATUSES,
  REQUEST_TYPE_META,
  SAMPLE_FILE_URL,
  STATUS_FILTERS,
  STATUS_META,
  downloadRunResultCsv,
  parseInvoiceIdsFile,
} from "./helpers";
import RequestDetailsModalComponent from "./RequestDetailsModalComponent";
import UploadPreviewComponent from "./UploadPreviewComponent";

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 5000;


const HEADER_BUTTON_CLASS =
  "inline-flex items-center justify-center w-full sm:!w-32 !h-9 !py-0 !px-4 !rounded-lg !text-[11px] whitespace-nowrap";

type Props = {
  requestType: InvoiceAdjustmentRequestType;
};

type IPreview = {
  fileName: string;
  invoiceIds: string[];
  summary: IInvoiceAdjustmentSummary;
};

const InvoiceAdjustmentComponent: React.FC<Props> = ({ requestType }) => {
  const meta = REQUEST_TYPE_META[requestType];
  const isBadDebt = requestType === "BAD_DEBT";
  const maxRows = INVOICE_ADJUSTMENT_MAX_ROWS[requestType];
  const { NO_CART } = useImageConstants();
  const { toastError } = useAPIUtils();
  const { veticDateFormat } = useUtils();
  
  const canUploadForceDiscount = useFeatureAccess("FORCE_DISCOUNT_UPLOAD_ACTION");
  const canUploadBadDebt = useFeatureAccess("BAD_DEBT_UPLOAD_ACTION");
  const canUpload = isBadDebt ? canUploadBadDebt : canUploadForceDiscount;
  const detailsModal = useModalActions("INVOICE_ADJUSTMENT_DETAILS_MODAL");
  const previewModal = useModalActions("INVOICE_ADJUSTMENT_PREVIEW_MODAL");
  const filterForm = useForm<{ status: string }>({ defaultValues: { status: "" } });

  const { execute: fetchSummary } = useInvoiceAdjustmentSummaryAPI();
  const { execute: uploadRequest } = useInvoiceAdjustmentUploadAPI();
  const { execute: fetchRequests } = useInvoiceAdjustmentRequestsAPI();
  const { execute: fetchRequestDetails } = useInvoiceAdjustmentRequestDetailsAPI();

  const [records, setRecords] = useState<IInvoiceAdjustmentRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<InvoiceAdjustmentRequestStatus | "">("");
  const [isListLoading, setIsListLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [preview, setPreview] = useState<IPreview | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(null);
  const [downloadingRequestId, setDownloadingRequestId] = useState<string | null>(null);

  const loadRequests = useCallback(
    async (page: number, silent = false) => {
      if (!silent) setIsListLoading(true);
      const { status, message } = await fetchRequests({
        request_type: requestType,
        status: statusFilter,
        page_number: page,
        page_size: PAGE_SIZE,
      });
      if (status && message) {
        setRecords(message.records);
        setTotalCount(message.total_count);
      } else if (!silent) {
        setRecords([]);
        setTotalCount(0);
      }
      if (!silent) setIsListLoading(false);
    },
    [fetchRequests, requestType, statusFilter],
  );

  useEffect(() => {
    loadRequests(currentPage);
  }, [loadRequests, currentPage]);

  const hasInFlight = records.some((record) => IN_FLIGHT_STATUSES.includes(record.status));
  useEffect(() => {
    if (!hasInFlight) return;
    const timer = window.setInterval(() => loadRequests(currentPage, true), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [hasInFlight, loadRequests, currentPage]);

  const onFileUpload = async (file: File): Promise<{ status: boolean; data?: IPreview }> => {
    const parsed = await parseInvoiceIdsFile(file, maxRows);
    if (parsed.error) {
      toastError(parsed.error);
      return { status: false };
    }
    const { status, message } = await fetchSummary({ request_type: requestType, invoice_ids: parsed.invoiceIds });
    if (!status || !message) return { status: false };
    return { status: true, data: { fileName: file.name, invoiceIds: parsed.invoiceIds, summary: message } };
  };

  const onSubmitUpload = async () => {
    if (!preview) return;
    setIsSubmitting(true);
    const result = await uploadRequest({ request_type: requestType, invoice_ids: preview.invoiceIds });
    setIsSubmitting(false);
    if (result.status) {
      previewModal.setInvisible();
      setPreview(null);
      if (currentPage === 1) {
        loadRequests(1);
      } else {
        setCurrentPage(1);
      }
    } else if (result.summary) {
      setPreview({ ...preview, summary: result.summary });
    }
  };

  const openPreview = (data: IPreview) => {
    setPreview(data);
    previewModal.setVisible();
  };

  const closePreview = () => {
    if (isSubmitting) return;
    previewModal.setInvisible();
    setPreview(null);
  };

  const openDetails = (requestId: string) => {
    setDetailsRequestId(requestId);
    detailsModal.setVisible();
  };

  const downloadResult = async (requestId: string) => {
    if (downloadingRequestId) return;
    setDownloadingRequestId(requestId);
    const { status, message } = await fetchRequestDetails(requestId);
    setDownloadingRequestId(null);
    if (status && message) {
      downloadRunResultCsv(message);
    } else {
      toastError("Could not download the result, try again");
    }
  };

  const statusItems = useMemo(
    () => STATUS_FILTERS[requestType].map((status) => ({ id: status, title: STATUS_META[status].label })),
    [requestType],
  );

  const columns: TableColumn<IInvoiceAdjustmentRequest>[] = [
    {
      id: "raised_at",
      header: "Uploaded On",
      minWidth: 150,
      cell: ({ row }) => <span className="whitespace-nowrap">{veticDateFormat(row.raised_at) ?? "-"}</span>,
    },
    {
      id: "raised_by",
      header: "Uploaded By",
      minWidth: 200,
      cell: ({ row }) => <span className="break-all">{row.raised_by_email || row.raised_by}</span>,
    },
    { id: "total_invoices", header: "Invoices", minWidth: 80, accessor: "total_invoices" },
    {
      id: "total_due_amount",
      header: "Due at Upload",
      minWidth: 120,
      cell: ({ row }) => <span className="whitespace-nowrap">{formatAmount(row.total_due_amount)}</span>,
    },
    {
      id: "amount_closed",
      header: "Amount Closed",
      minWidth: 120,
      cell: ({ row }) =>
        FINISHED_STATUSES.includes(row.status)
          ? formatAmount(row.invoice_data.reduce((total, item) => total + (item.amount_closed || 0), 0))
          : "-",
    },
    {
      id: "counts",
      header: "Success / Failed / Invalid",
      minWidth: 180,
      cell: ({ row }) =>
        FINISHED_STATUSES.includes(row.status)
          ? `${row.success_count} / ${row.failed_count} / ${row.invalid_count ?? 0}`
          : "-",
    },
    {
      id: "status",
      header: "Status",
      minWidth: 170,
      cell: ({ row }) => {
        const statusMeta = STATUS_META[row.status];
        return (
          <StatusBadge
            label={statusMeta?.label ?? row.status}
            colorClassName={statusMeta?.colorClassName}
            statusFillClassname={statusMeta?.fillClassName}
          />
        );
      },
    },
    {
      id: "action",
      header: "Action",
      minWidth: 150,
      cell: ({ row }) => (
        <div className="flex gap-2 items-center whitespace-nowrap text-xs">
          <button
            type="button"
            className="text-blue-600 hover:text-blue-700 hover:underline"
            onClick={() => openDetails(row.request_id)}
          >
            View
          </button>
          {FINISHED_STATUSES.includes(row.status) ? (
            <>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                className="text-blue-600 hover:text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                disabled={!!downloadingRequestId}
                onClick={() => downloadResult(row.request_id)}
              >
                {downloadingRequestId === row.request_id ? "Downloading..." : "Download"}
              </button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="h-full">
      <div className="pt-3 pb-2 rounded-lg bg-white px-2">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center border-b pb-2 gap-2">
          <div>
            <p className="text-sm font-semibold mt-2">{meta.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{meta.description}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="w-full sm:w-52">
              <NonSearchableSelectMenu
                errorMessages={[]}
                fieldError={undefined}
                register={filterForm.register("status")}
                selectItems={statusItems}
                placeholder="All statuses"
                withReset
                showTooltips={false}
                showDropdownIcon
                onSelectItem={(item) => {
                  setStatusFilter((item?.id as InvoiceAdjustmentRequestStatus) ?? "");
                  setCurrentPage(1);
                }}
              />
            </div>
            <SecondaryButton
              size="MD"
              asType="button"
              className={HEADER_BUTTON_CLASS}
              onButtonClick={() => loadRequests(currentPage)}
            >
              Refresh
            </SecondaryButton>
            {canUpload ? (
              <SolidButton
                size="MD"
                asType="button"
                className={HEADER_BUTTON_CLASS}
                onButtonClick={() => setIsUploadModalOpen(true)}
                disabled={isSubmitting}
              >
                Upload File
              </SolidButton>
            ) : null}
          </div>
        </div>
        {canUpload ? (
          <p className="text-[11px] text-gray-500 pt-2">
            CSV or Excel with one column, <span className="font-medium">invoice_id</span>, and{" "}
            <span className="font-semibold text-[#FF0303]">up to {maxRows} rows</span>.
          </p>
        ) : null}
      </div>

      <div className="mt-2 pb-2 rounded-lg bg-white px-2 pt-2">
        <p className="text-xs text-gray-600 pb-2">
          <span className="font-medium text-black">{meta.title} requests</span>
          {totalCount > 0 ? <span> · {totalCount} total</span> : null}
        </p>
        <TableComponent<IInvoiceAdjustmentRequest>
          columns={columns}
          data={records}
          getRowId={(row) => row.request_id}
          minTableWidthClassName="min-w-[1180px]"
          loading={isListLoading}
          loadingState={<TableSkeleton />}
          emptyState={
            <div className="h-[360px]">
              <NoItemFoundComponent
                message=""
                description={`No ${meta.title} requests found`}
                imageURL={NO_CART}
                InputClassName="my-0 h-full"
              />
            </div>
          }
          stickyHeader
          scrollContainerClassName="max-h-[520px] sm:max-h-[650px]"
        />
        {totalCount > PAGE_SIZE ? (
          <div className="flex justify-end pt-2">
            <PaginationComponent
              className="pagination-bar"
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
            />
          </div>
        ) : null}
      </div>

      <GenericBulkUploadModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={onFileUpload}
        onSuccess={(data: IPreview) => openPreview(data)}
        title={`${meta.title}: Upload Invoice IDs`}
        sampleDownloadUrl={SAMPLE_FILE_URL}
      />
      <UploadPreviewComponent
        fileName={preview?.fileName ?? ""}
        summary={preview?.summary ?? null}
        title={`${meta.title}: Preview`}
        submitLabel={meta.submitLabel}
        isSubmitting={isSubmitting}
        onCancel={closePreview}
        onSubmit={onSubmitUpload}
      />
      <RequestDetailsModalComponent requestId={detailsRequestId} onClose={() => setDetailsRequestId(null)} />
    </div>
  );
};

export default InvoiceAdjustmentComponent;
